/**
 * A small syntax highlighter.
 *
 * Not a full parser and not trying to be: it colours strings, comments,
 * numbers, keywords and a few shapes, which is what makes a pasted snippet
 * readable in a chat transcript. Anything it does not recognise renders as
 * plain text, so an unknown language degrades to a monospace block rather
 * than to garbage.
 *
 * Hand-written to avoid pulling a highlighting library — and its per-language
 * grammars — into the bundle for a feature that appears a few times a page.
 */

export interface Token {
  value: string;
  /** Tailwind classes, or empty for plain text. */
  className: string;
}

type Rule = { pattern: RegExp; className: string };

const COLORS = {
  comment: 'text-slate-500 italic',
  string: 'text-emerald-300',
  number: 'text-amber-300',
  keyword: 'text-violet-300',
  literal: 'text-sky-300',
  function: 'text-brand-200',
  tag: 'text-rose-300',
  attr: 'text-amber-200',
  property: 'text-sky-200',
};

const JS_KEYWORDS =
  'await|async|break|case|catch|class|const|continue|debugger|default|delete|do|else|export|extends|finally|for|from|function|if|implements|import|in|instanceof|interface|let|new|of|private|protected|public|readonly|return|satisfies|static|super|switch|this|throw|try|type|typeof|var|void|while|yield|enum|declare|namespace|as|is|keyof|infer';

const PY_KEYWORDS =
  'and|as|assert|async|await|break|class|continue|def|del|elif|else|except|finally|for|from|global|if|import|in|is|lambda|nonlocal|not|or|pass|raise|return|try|while|with|yield';

const GO_KEYWORDS =
  'break|case|chan|const|continue|default|defer|else|fallthrough|for|func|go|goto|if|import|interface|map|package|range|return|select|struct|switch|type|var';

const SQL_KEYWORDS =
  'select|from|where|join|left|right|inner|outer|on|group|by|order|having|limit|offset|insert|into|values|update|set|delete|create|table|alter|drop|index|as|and|or|not|null|distinct|union|all|case|when|then|end';

const RUST_KEYWORDS =
  'as|async|await|break|const|continue|crate|dyn|else|enum|extern|false|fn|for|if|impl|in|let|loop|match|mod|move|mut|pub|ref|return|self|static|struct|super|trait|true|type|unsafe|use|where|while';

const SHELL_KEYWORDS =
  'if|then|else|elif|fi|for|while|do|done|case|esac|function|return|export|source|local|echo|cd|set';

/** Rules are tried in order; the earliest match in the text wins. */
function rulesFor(language?: string): Rule[] {
  const lang = (language ?? '').toLowerCase();

  const common = {
    lineComment: (prefix: string): Rule => ({
      pattern: new RegExp(`${prefix}[^\\n]*`),
      className: COLORS.comment,
    }),
    blockComment: { pattern: /\/\*[\s\S]*?\*\//, className: COLORS.comment },
    doubleQuoted: { pattern: /"(?:[^"\\\n]|\\.)*"/, className: COLORS.string },
    singleQuoted: { pattern: /'(?:[^'\\\n]|\\.)*'/, className: COLORS.string },
    backtick: { pattern: /`(?:[^`\\]|\\.)*`/, className: COLORS.string },
    number: { pattern: /\b\d[\d_]*(?:\.\d+)?(?:e[+-]?\d+)?\b/i, className: COLORS.number },
  };

  const keywordRule = (words: string): Rule => ({
    pattern: new RegExp(`\\b(?:${words})\\b`),
    className: COLORS.keyword,
  });

  switch (lang) {
    case 'json':
      return [
        { pattern: /"(?:[^"\\\n]|\\.)*"(?=\s*:)/, className: COLORS.property },
        common.doubleQuoted,
        { pattern: /\b(?:true|false|null)\b/, className: COLORS.literal },
        common.number,
      ];

    case 'py':
    case 'python':
      return [
        common.lineComment('#'),
        { pattern: /"""[\s\S]*?"""/, className: COLORS.string },
        common.doubleQuoted,
        common.singleQuoted,
        keywordRule(PY_KEYWORDS),
        { pattern: /\b(?:True|False|None|self)\b/, className: COLORS.literal },
        { pattern: /\b[a-z_]\w*(?=\()/i, className: COLORS.function },
        common.number,
      ];

    case 'go':
      return [
        common.lineComment('//'),
        common.blockComment,
        common.doubleQuoted,
        common.backtick,
        keywordRule(GO_KEYWORDS),
        { pattern: /\b(?:true|false|nil|iota|error|string|int|int64|bool|byte|rune)\b/, className: COLORS.literal },
        { pattern: /\b[a-z_]\w*(?=\()/i, className: COLORS.function },
        common.number,
      ];

    case 'rs':
    case 'rust':
      return [
        common.lineComment('//'),
        common.blockComment,
        common.doubleQuoted,
        keywordRule(RUST_KEYWORDS),
        { pattern: /\b(?:Some|None|Ok|Err|Vec|String|Option|Result)\b/, className: COLORS.literal },
        { pattern: /\b[a-z_]\w*(?=\()/i, className: COLORS.function },
        common.number,
      ];

    case 'sql':
      return [
        common.lineComment('--'),
        common.singleQuoted,
        { pattern: new RegExp(`\\b(?:${SQL_KEYWORDS})\\b`, 'i'), className: COLORS.keyword },
        common.number,
      ];

    case 'sh':
    case 'bash':
    case 'shell':
    case 'zsh':
      return [
        common.lineComment('#'),
        common.doubleQuoted,
        common.singleQuoted,
        { pattern: /\$\{?\w+\}?/, className: COLORS.literal },
        keywordRule(SHELL_KEYWORDS),
        { pattern: /(?:^|\n)\s*[a-z_][\w-]*(?=\s)/i, className: COLORS.function },
      ];

    case 'html':
    case 'xml':
    case 'svg':
      return [
        { pattern: /<!--[\s\S]*?-->/, className: COLORS.comment },
        { pattern: /<\/?[a-z][\w:-]*/i, className: COLORS.tag },
        { pattern: /[a-z-]+(?==)/i, className: COLORS.attr },
        common.doubleQuoted,
        common.singleQuoted,
      ];

    case 'css':
    case 'scss':
      return [
        common.blockComment,
        { pattern: /[.#][\w-]+/, className: COLORS.tag },
        { pattern: /[a-z-]+(?=\s*:)/i, className: COLORS.property },
        common.doubleQuoted,
        common.singleQuoted,
        { pattern: /\b\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw|s|ms)?\b/, className: COLORS.number },
      ];

    case 'yml':
    case 'yaml':
      return [
        common.lineComment('#'),
        { pattern: /^\s*[\w.-]+(?=\s*:)/m, className: COLORS.property },
        common.doubleQuoted,
        common.singleQuoted,
        { pattern: /\b(?:true|false|null|yes|no)\b/i, className: COLORS.literal },
        common.number,
      ];

    case 'md':
    case 'markdown':
      return [
        { pattern: /^#{1,6}\s.*$/m, className: COLORS.keyword },
        { pattern: /\*\*[^*]+\*\*/, className: COLORS.property },
        { pattern: /`[^`]+`/, className: COLORS.string },
      ];

    case 'diff':
      return [
        { pattern: /^\+[^\n]*/m, className: 'text-emerald-300' },
        { pattern: /^-[^\n]*/m, className: 'text-red-300' },
        { pattern: /^@@[^\n]*/m, className: COLORS.keyword },
      ];

    default:
      // The JavaScript family, and a reasonable guess for anything C-like.
      return [
        common.lineComment('//'),
        common.blockComment,
        common.doubleQuoted,
        common.singleQuoted,
        common.backtick,
        keywordRule(JS_KEYWORDS),
        { pattern: /\b(?:true|false|null|undefined|NaN|Infinity)\b/, className: COLORS.literal },
        { pattern: /\b[a-z_$][\w$]*(?=\()/i, className: COLORS.function },
        common.number,
      ];
  }
}

/** Splits source into coloured and plain runs. */
export function highlight(source: string, language?: string): Token[] {
  const rules = rulesFor(language);
  const tokens: Token[] = [];
  let rest = source;
  // Bounded so a pathological pattern cannot lock the tab up.
  let guard = 0;

  while (rest && guard < 20_000) {
    guard += 1;

    let best: { index: number; length: number; className: string } | null = null;
    for (const rule of rules) {
      const match = rule.pattern.exec(rest);
      if (!match || match[0].length === 0) continue;
      if (best === null || match.index < best.index) {
        best = { index: match.index, length: match[0].length, className: rule.className };
      }
      if (best.index === 0) break;
    }

    if (!best) break;
    if (best.index > 0) tokens.push({ value: rest.slice(0, best.index), className: '' });
    tokens.push({
      value: rest.slice(best.index, best.index + best.length),
      className: best.className,
    });
    rest = rest.slice(best.index + best.length);
  }

  if (rest) tokens.push({ value: rest, className: '' });
  return tokens;
}
